"use client"

import React, { useCallback, useEffect, useReducer, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { Loader2, X } from "lucide-react"
import { Sheet, SheetPortal, SheetOverlay } from "@/components/ui/sheet"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { useApi } from "@/lib/api"
import type { OBRequestSummary, OBRequestTask, OBRequestType, OBTaskStatus, OBTaskActor } from "@/types/workflow"
import { useTranslation } from "@/hooks/use-translation"
import { AvatarCluster } from "./request-progress/avatar-cluster"
import { LegendStrip } from "./request-progress/legend-strip"
import { calcSectionDuration, calcTaskDuration, formatDurationCompact, formatPanelDate, resolveTaskCompletedAt } from "./request-progress/duration-utils"
import { formatDateTime } from "@/utils/format-helpers"
import { useLanguage } from "@/contexts/language-context"

// ─── Request task deduplication cache ─────────────────────────────────────────
// Keyed by request ID. Stores the in-flight promise so StrictMode's second
// effect invocation reuses it instead of firing a second HTTP call.
const inflightTaskRequests = new Map<string, Promise<OBRequestTask[]>>()

// ─── Constants ────────────────────────────────────────────────────────────────

const COMPACT_WIDTH = 430
const FULL_WIDTH = 830
const SNAP_THRESHOLD_RATIO = 0.42
const STORAGE_WIDTH_KEY = "rpp-drawer-width"
const STORAGE_SECTION_KEY = (rt: string) => `rpp-section-collapsed-${rt}`

const REQUEST_TYPE_LABELS: Record<OBRequestType, string> = {
  Onboarding:      "Onboarding",
  Extension:       "Extension",
  ChangeRequest:   "Change Request",
  DocumentRequest: "Document Request",
}
const REQUEST_TYPE_ORDER: OBRequestType[] = ["Onboarding", "Extension", "ChangeRequest", "DocumentRequest"]

const TASK_STATUS_CONFIG: Record<OBTaskStatus, { label: string; color: string; bg: string }> = {
  Completed:        { label: "Completed",        color: "#15803D", bg: "#DCFCE7" },
  InProgress:       { label: "In Progress",       color: "#1D4ED8", bg: "#DBEAFE" },
  RequiresApproval: { label: "Req. Approval",     color: "#92400E", bg: "#FEF3C7" },
  RequiresReview:   { label: "Req. Review",       color: "#92400E", bg: "#FEF3C7" },
  New:              { label: "New",               color: "#475569", bg: "#F1F5F9" },
  Returned:         { label: "Returned",          color: "#C2410C", bg: "#FFEDD5" },
  Declined:         { label: "Declined",          color: "#B91C1C", bg: "#FEE2E2" },
  Discarded:        { label: "Discarded",         color: "#B91C1C", bg: "#FEE2E2" },
}

// ─── Column layout helpers ─────────────────────────────────────────────────────

const COL = {
  id:        36,
  startDate: 90,
  endDate:   90,
  duration:  66,
  avatars:   80,
  status:    110,
} as const

// ─── Sub-components ────────────────────────────────────────────────────────────

function ActorStatusTooltip({
  actors,
  pos,
  onMouseEnter,
  onMouseLeave,
}: {
  actors: OBTaskActor[]
  pos: { left: number; top?: number; bottom?: number }
  onMouseEnter: () => void
  onMouseLeave: () => void
}) {
  const { t } = useTranslation()
  const { language } = useLanguage()

  const content = (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        bottom: pos.bottom,
        zIndex: 9999,
        minWidth: 240,
        maxWidth: 320,
        backgroundColor: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 8,
        boxShadow: "0 4px 20px rgba(0,0,0,0.14)",
        padding: "8px 0",
        pointerEvents: "auto",
      }}
    >
      <p style={{ padding: "2px 12px 6px", fontSize: 10, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {t('rppActorDetails')}
      </p>
      {actors.map((actor, i) => {
        const cfg = TASK_STATUS_CONFIG[actor.status] ?? TASK_STATUS_CONFIG.New
        const completedDate = actor.status === "Completed" && actor.completedAt
          ? formatDateTime(actor.completedAt, language)
          : null
        return (
          <div key={i} style={{ padding: "5px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12, color: "#1E293B", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                {actor.name}
              </span>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 10, fontWeight: 600, color: cfg.color,
                whiteSpace: "nowrap", flexShrink: 0,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: cfg.color }} />
                {cfg.label}
              </span>
            </div>
            {completedDate && (
              <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1, paddingLeft: 0 }}>
                {t('rppCompletedOn')} {completedDate}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  if (typeof document === "undefined") return null
  return ReactDOM.createPortal(content, document.body)
}

const STATUS_SHOW_DELAY = 120
const STATUS_HIDE_DELAY = 80

function StatusBadge({ status, actors }: { status: OBTaskStatus; actors?: OBTaskActor[] }) {
  const cfg = TASK_STATUS_CONFIG[status] ?? { label: status, color: "#475569", bg: "#F1F5F9" }
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null)
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasActors = actors && actors.length > 0

  const computePos = useCallback(() => {
    if (!anchorRef.current) return null
    const rect = anchorRef.current.getBoundingClientRect()
    const tooltipHeight = 160
    const above = rect.bottom + tooltipHeight > window.innerHeight
    const left = Math.max(8, rect.right - 280)
    if (above) return { left, bottom: window.innerHeight - rect.top + 6 }
    return { left, top: rect.bottom + 6 }
  }, [])

  const scheduleShow = useCallback(() => {
    if (!hasActors) return
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
    if (pos) return
    showTimerRef.current = setTimeout(() => setPos(computePos()), STATUS_SHOW_DELAY)
  }, [computePos, pos, hasActors])

  const scheduleHide = useCallback(() => {
    if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null }
    hideTimerRef.current = setTimeout(() => setPos(null), STATUS_HIDE_DELAY)
  }, [])

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
  }, [])

  return (
    <>
      <span
        ref={anchorRef}
        tabIndex={hasActors ? 0 : undefined}
        role={hasActors ? "button" : undefined}
        onMouseEnter={scheduleShow}
        onMouseLeave={scheduleHide}
        onFocus={scheduleShow}
        onBlur={scheduleHide}
        onClick={hasActors ? () => { if (pos) { cancelHide(); scheduleHide() } else { scheduleShow() } } : undefined}
        onKeyDown={hasActors ? (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            if (pos) { cancelHide(); scheduleHide() } else { scheduleShow() }
          }
        } : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 9999,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 600,
          color: cfg.color,
          backgroundColor: cfg.bg,
          whiteSpace: "nowrap",
          cursor: hasActors ? "default" : undefined,
          outline: "none",
        }}
      >
        {cfg.label}
      </span>
      {pos && hasActors && (
        <ActorStatusTooltip
          actors={actors}
          pos={pos}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
        />
      )}
    </>
  )
}

function DateCell({ iso }: { iso: string | null | undefined }) {
  const parts = formatPanelDate(iso)
  if (!parts) return <span style={{ color: "#CBD5E1" }}>—</span>
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <span style={{ fontSize: 11, color: "#1E293B" }}>{parts.date}</span>
      <span style={{ fontSize: 10, color: "#94A3B8" }}>{parts.time}</span>
    </div>
  )
}

function DurationCell({ task }: { task: OBRequestTask }) {
  const result = calcTaskDuration(task)
  if (result.variant === "none" || result.ms === null) {
    return <span style={{ color: "#CBD5E1" }}>—</span>
  }
  const compact = formatDurationCompact(result.ms)
  if (result.variant === "elapsed") {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 3,
        borderRadius: 9999, padding: "2px 6px",
        fontSize: 11,
        color: "#92400E", backgroundColor: "#FEF3C7",
        whiteSpace: "nowrap",
      }}>
        <span style={{ fontWeight: 700 }}>⏱</span>
        <span style={{ fontWeight: 400 }}>{compact}</span>
      </span>
    )
  }
  return <span style={{ fontSize: 11, fontWeight: 400, color: "#64748B", whiteSpace: "nowrap" }}>{compact}</span>
}

function SectionDurationRow({ tasks }: { tasks: OBRequestTask[] }) {
  const result = calcSectionDuration(tasks)
  if (!result) return null

  const isElapsed = result.variant === "elapsed"
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        borderRadius: 9999, padding: "2px 8px",
        fontSize: 11, fontWeight: 400,
        color: isElapsed ? "#92400E" : "#475569",
        backgroundColor: isElapsed ? "#FEF3C7" : "#F1F5F9",
      }}>
        {isElapsed
          ? <><span style={{ fontWeight: 700 }}>⏱</span>{" Elapsed · "}{result.formatted}{" · still in progress"}</>
          : `Total duration · ${result.formatted}`}
      </span>
    </div>
  )
}

function ProgressBar({ completed, total, isCompleted }: { completed: number; total: number; isCompleted: boolean }) {
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100)
  const fillColor = isCompleted ? "#3BD4AE" : "#005CB9"
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 4, borderRadius: 2, backgroundColor: "#E2E8F0", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", backgroundColor: fillColor, borderRadius: 2, transition: "width 0.3s ease" }} />
      </div>
      <span style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap" }}>{completed} / {total}</span>
    </div>
  )
}

// ─── TaskRow ──────────────────────────────────────────────────────────────────

function TaskRow({ task, isFull, index }: { task: OBRequestTask; isFull: boolean; index: number }) {
  const completedAt = resolveTaskCompletedAt(task)
  const rowBg = index % 2 === 0 ? "#FFFFFF" : "#FAFAFA"

  return (
    <tr style={{ backgroundColor: rowBg }}>
      <td style={{ width: COL.id, padding: "6px 8px 6px 12px", color: "#94A3B8", fontSize: 12, fontWeight: 500, whiteSpace: "nowrap" }}>
        {task.order != null ? task.order : "—"}
      </td>
      <td style={{ padding: "6px 8px", fontSize: 13, color: "#1E293B", maxWidth: 0 }}>
        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={task.taskName}>
          {task.taskName ?? "—"}
        </span>
      </td>
      {isFull && (
        <>
          <td style={{ width: COL.startDate, padding: "6px 8px" }}>
            <DateCell iso={task.startDateTime} />
          </td>
          <td style={{ width: COL.endDate, padding: "6px 8px" }}>
            <DateCell iso={completedAt} />
          </td>
          <td style={{ width: COL.duration, padding: "6px 8px" }}>
            <DurationCell task={task} />
          </td>
        </>
      )}
      <td style={{ width: COL.avatars, padding: "6px 8px" }}>
        <AvatarCluster actors={task.actors ?? []} />
      </td>
      <td style={{ width: COL.status, padding: "6px 12px 6px 8px", textAlign: "right" }}>
        <StatusBadge status={task.taskStatus} actors={task.actors} />
      </td>
    </tr>
  )
}

// ─── Column headers ────────────────────────────────────────────────────────────

function ColumnHeaders({ isFull }: { isFull: boolean }) {
  const th = (label: string, width?: number, align: "left" | "right" = "left") => (
    <th style={{
      width,
      padding: "6px 8px",
      fontSize: 10,
      fontWeight: 700,
      color: "#334155",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      textAlign: align,
      whiteSpace: "nowrap",
      borderTop: "1px solid #E2E8F0",
      borderBottom: "2px solid #E2E8F0",
      backgroundColor: "#F1F5F9",
    }}>
      {label}
    </th>
  )

  return (
    <tr>
      {th("", COL.id)}
      {th("TASK")}
      {isFull && th("START DATE", COL.startDate)}
      {isFull && th("COMPLETED", COL.endDate)}
      {isFull && th("DURATION", COL.duration)}
      {th("", COL.avatars)}
      {th("STATUS", COL.status, "right")}
    </tr>
  )
}

// ─── RequestSection ────────────────────────────────────────────────────────────

function RequestSection({
  request,
  syncApi,
  isFull,
  autoExpand = false,
}: {
  request: OBRequestSummary
  syncApi: ReturnType<typeof useApi>["syncApi"]
  isFull: boolean
  autoExpand?: boolean
}) {
  // Key by request.id so multiple same-type sections each get their own state
  const storageKey = `rpp-section-${request.id}`
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored !== null) return stored === "true"
      return !autoExpand
    } catch {
      return !autoExpand
    }
  })
  const [tasks, setTasks] = useState<OBRequestTask[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)

    // Reuse an in-flight promise for this request ID if one already exists
    // (e.g. React StrictMode's double-invoke) to avoid duplicate HTTP calls.
    if (!inflightTaskRequests.has(request.id)) {
      const promise = syncApi
        .get<OBRequestTask[]>(`RequestTask/by-request/${request.id}`)
        .catch(() => [] as OBRequestTask[])
      inflightTaskRequests.set(request.id, promise)
      promise.finally(() => inflightTaskRequests.delete(request.id))
    }

    inflightTaskRequests.get(request.id)!
      .then(result => {
        if (controller.signal.aborted) return
        setTasks((result ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
      })
      .catch(() => {})
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })

    return () => controller.abort()
  }, [request.id, syncApi])

  const toggleCollapsed = useCallback(() => {
    setCollapsed(v => {
      const next = !v
      try { localStorage.setItem(storageKey, String(next)) } catch {}
      return next
    })
  }, [storageKey])

  const completedCount = tasks.filter(t => t.taskStatus === "Completed").length
  const isCompleted = request.status === "Completed"
  const accentColor = isCompleted ? "#3BD4AE" : "#005CB9"
  const label = REQUEST_TYPE_LABELS[request.requestType] ?? request.requestType

  return (
    <div style={{
      marginBottom: 8,
      borderRadius: 8,
      border: "1px solid #E2E8F0",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      {/* Section header — fully clickable */}
      <div
        role="button"
        tabIndex={0}
        onClick={toggleCollapsed}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") toggleCollapsed() }}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "10px 12px 8px",
          cursor: "pointer",
          userSelect: "none",
          backgroundColor: "#F8FAFC",
          borderLeft: `3px solid ${accentColor}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{
              fontSize: 11,
              color: "#64748B",
              display: "inline-block",
              transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
              lineHeight: 1,
            }}>
              ▼
            </span>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{label}</span>
            {loading && <Loader2 style={{ width: 12, height: 12, color: "#94A3B8", animation: "spin 1s linear infinite" }} />}
          </div>
          {/* Duration sub-row — hidden when collapsed */}
          {!collapsed && tasks.length > 0 && <SectionDurationRow tasks={tasks} />}
        </div>
        {/* Progress bar on the right */}
        <div style={{ width: 160, marginLeft: 12, paddingTop: 2, flexShrink: 0 }}>
          <ProgressBar completed={completedCount} total={tasks.length} isCompleted={isCompleted} />
        </div>
      </div>

      {/* Expandable content */}
      {!collapsed && (
        <>
          <LegendStrip tasks={tasks} />
          <div style={{ overflowX: "hidden", paddingBottom: 20 }}>
            <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
              <colgroup>
                <col style={{ width: COL.id }} />
                <col /> {/* flex name */}
                {isFull && <col style={{ width: COL.startDate }} />}
                {isFull && <col style={{ width: COL.endDate }} />}
                {isFull && <col style={{ width: COL.duration }} />}
                <col style={{ width: COL.avatars }} />
                <col style={{ width: COL.status }} />
              </colgroup>
              <thead>
                <ColumnHeaders isFull={isFull} />
              </thead>
              <tbody>
                {tasks.length === 0 && !loading && (
                  <tr>
                    <td colSpan={isFull ? 7 : 4} style={{ padding: "12px 16px", fontSize: 12, color: "#94A3B8", fontStyle: "italic", textAlign: "center" }}>
                      No tasks found.
                    </td>
                  </tr>
                )}
                {tasks.map((task, i) => (
                  <TaskRow key={task.id} task={task} isFull={isFull} index={i} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Fetch state ───────────────────────────────────────────────────────────────

type FetchState = { loading: boolean; error: string | null; requests: OBRequestSummary[] }
type FetchAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; requests: OBRequestSummary[] }
  | { type: "FETCH_ERROR"; error: string }

function fetchReducer(_: FetchState, action: FetchAction): FetchState {
  switch (action.type) {
    case "FETCH_START":   return { loading: true,  error: null,         requests: [] }
    case "FETCH_SUCCESS": return { loading: false, error: null,         requests: action.requests }
    case "FETCH_ERROR":   return { loading: false, error: action.error, requests: [] }
  }
}

// ─── Drag-resize hook ──────────────────────────────────────────────────────────

function useResizableDrawer() {
  const savedMode = (): "compact" | "full" => {
    try { return localStorage.getItem(STORAGE_WIDTH_KEY) === "full" ? "full" : "compact" } catch { return "compact" }
  }

  const [mode, setMode] = useState<"compact" | "full">(savedMode)
  const [dragging, setDragging] = useState(false)
  const [dragWidth, setDragWidth] = useState<number | null>(null)
  const startRef = useRef<{ x: number; startWidth: number } | null>(null)

  const currentWidth = dragging && dragWidth !== null ? dragWidth : (mode === "full" ? FULL_WIDTH : COMPACT_WIDTH)

  const onHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startWidth = mode === "full" ? FULL_WIDTH : COMPACT_WIDTH
    startRef.current = { x: e.clientX, startWidth }
    setDragging(true)
    setDragWidth(startWidth)
  }, [mode])

  useEffect(() => {
    if (!dragging) return

    const onMove = (e: MouseEvent) => {
      if (!startRef.current) return
      // Dragging left increases width (handle is on the left edge of the panel)
      const delta = startRef.current.x - e.clientX
      const raw = Math.min(FULL_WIDTH, Math.max(COMPACT_WIDTH, startRef.current.startWidth + delta))
      setDragWidth(raw)
    }

    const onUp = (e: MouseEvent) => {
      if (!startRef.current) return
      const delta = startRef.current.x - e.clientX
      const startedFull = startRef.current.startWidth === FULL_WIDTH

      let nextMode: "compact" | "full"
      if (Math.abs(delta) < 5) {
        // Treat as a click — simply toggle
        nextMode = startedFull ? "compact" : "full"
      } else {
        const range = FULL_WIDTH - COMPACT_WIDTH
        const threshold = range * SNAP_THRESHOLD_RATIO
        if (startedFull) {
          nextMode = delta < -threshold ? "compact" : "full"
        } else {
          nextMode = delta > threshold ? "full" : "compact"
        }
      }

      setMode(nextMode)
      try { localStorage.setItem(STORAGE_WIDTH_KEY, nextMode) } catch {}
      setDragging(false)
      setDragWidth(null)
      startRef.current = null
    }

    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
    return () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
    }
  }, [dragging])

  return { currentWidth, dragging, onHandleMouseDown, isFull: mode === "full" }
}

// ─── Drag handle ───────────────────────────────────────────────────────────────

function DragHandle({ onMouseDown, isFull }: { onMouseDown: (e: React.MouseEvent) => void; isFull: boolean }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={isFull ? "Collapse panel" : "Expand panel"}
      style={{
        position: "absolute",
        left: -18,
        top: "50%",
        transform: "translateY(-50%)",
        width: 18,
        height: 64,
        cursor: "pointer",
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      <div style={{
        width: 14,
        height: 64,
        borderRadius: "6px 0 0 6px",
        backgroundColor: hovered ? "#3BD4AE" : "#E2E8F0",
        border: hovered ? "none" : "1px solid #CBD5E1",
        borderRight: "none",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        boxShadow: hovered ? "-2px 0 8px rgba(59,212,174,0.25)" : "-1px 0 4px rgba(0,0,0,0.06)",
      }}>
        {/* Direction arrows */}
        <span style={{
          fontSize: 9,
          lineHeight: 1,
          color: hovered ? "#FFFFFF" : "#94A3B8",
          fontWeight: 700,
          letterSpacing: -1,
          transition: "color 0.15s ease",
          userSelect: "none",
        }}>
          {isFull ? "»" : "«"}
        </span>
        {/* Grip dots */}
        {[0, 1, 2, 3, 4].map(i => (
          <span key={i} style={{
            width: 3,
            height: 3,
            borderRadius: "50%",
            backgroundColor: hovered ? "rgba(255,255,255,0.7)" : "#B0BEC5",
            flexShrink: 0,
            transition: "background-color 0.15s ease",
          }} />
        ))}
        {/* Direction arrows (mirrored at bottom for balance) */}
        <span style={{
          fontSize: 9,
          lineHeight: 1,
          color: hovered ? "#FFFFFF" : "#94A3B8",
          fontWeight: 700,
          letterSpacing: -1,
          transition: "color 0.15s ease",
          userSelect: "none",
        }}>
          {isFull ? "»" : "«"}
        </span>
      </div>
    </div>
  )
}

// ─── Main panel ────────────────────────────────────────────────────────────────

interface RequestProgressPanelProps {
  isOpen: boolean
  onClose: () => void
  fboId: number | undefined
  entityType?: string
  workerName?: string
}

export function RequestProgressPanel({ isOpen, onClose, fboId, entityType = 'WorkOrder', workerName }: RequestProgressPanelProps) {
  const { t } = useTranslation()
  const { syncApi } = useApi()
  const [{ loading, error, requests }, dispatch] = useReducer(fetchReducer, {
    loading: false,
    error: null,
    requests: [],
  })
  const { currentWidth, dragging, onHandleMouseDown, isFull } = useResizableDrawer()

  useEffect(() => {
    if (!isOpen || !fboId) return
    let cancelled = false

    const timer = setTimeout(() => {
      if (cancelled) return
      dispatch({ type: "FETCH_START" })
      syncApi.get<OBRequestSummary[]>(`Request/existing?entityType=${encodeURIComponent(entityType)}&entityId=${fboId}&requestType=`, { skipRetryOn401: true } as any)
        .then(result => {
          if (cancelled) return
          const sorted = [...(result ?? [])].sort(
            (a, b) => REQUEST_TYPE_ORDER.indexOf(a.requestType) - REQUEST_TYPE_ORDER.indexOf(b.requestType)
          )
          dispatch({ type: "FETCH_SUCCESS", requests: sorted })
        })
        .catch(e => {
          if (cancelled) return
          dispatch({ type: "FETCH_ERROR", error: e?.message ?? "Failed to load request data." })
        })
    }, 500)

    return () => { cancelled = true; clearTimeout(timer) }
  }, [isOpen, fboId])

  return (
    <Sheet open={isOpen} onOpenChange={open => { if (!open) onClose() }}>
      <SheetPortal>
        <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <SheetPrimitive.Content
          style={{
            width: currentWidth,
            transition: dragging ? "none" : "width 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
            position: "fixed",
            insetBlock: 0,
            right: 0,
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
            boxShadow: "-4px 0 24px rgba(0,0,0,0.10)",
            overflow: "visible",
          }}
        >
          {/* Clip the content but not the drag handle that protrudes left */}
          <DragHandle onMouseDown={onHandleMouseDown} isFull={isFull} />

          {/* Header */}
          <div style={{
            flexShrink: 0,
            padding: "14px 16px 12px",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
          }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
              {t('rppTitle')}
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>
                {workerName ?? "—"}
              </span>
              <SheetPrimitive.Close
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  color: "#64748B",
                  flexShrink: 0,
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#F1F5F9")}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <X style={{ width: 16, height: 16 }} />
                <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>{t('close')}</span>
              </SheetPrimitive.Close>
            </div>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0, position: "relative" }}>
            {loading && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 16px", gap: 8, color: "#94A3B8" }}>
                <Loader2 style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} />
                <span style={{ fontSize: 13 }}>{t('rppLoadingRequests')}</span>
              </div>
            )}

            {!loading && error && (
              <div style={{ margin: 16, padding: "10px 14px", borderRadius: 8, backgroundColor: "#FEE2E2", border: "1px solid #FECACA", fontSize: 13, color: "#B91C1C" }}>
                {t('rppLoadError')}
              </div>
            )}

            {!loading && !error && requests.length === 0 && (
              <p style={{ textAlign: "center", padding: "48px 16px", fontSize: 13, color: "#94A3B8", fontStyle: "italic" }}>
                {t('rppNoRequests')}
              </p>
            )}

            {!loading && !error && requests.length > 0 && (
              <div style={{ padding: "8px 12px 12px" }}>
                {[...requests]
                  .sort((a, b) => {
                    const aCompleted = a.status === "Completed" ? 1 : 0
                    const bCompleted = b.status === "Completed" ? 1 : 0
                    return aCompleted - bCompleted
                  })
                  .map(request => (
                    <RequestSection
                      key={request.id}
                      request={request}
                      syncApi={syncApi}
                      isFull={isFull}
                      autoExpand={request.status !== "Completed"}
                    />
                  ))}
              </div>
            )}
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  )
}
